/**
 * Module: web/src/help/tips.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 2.13.1
 * Date: 2026-10-30
 *
 * v2.13.1 (exact-coordinates review fix): 'reports.geo_plotting' no longer calls every solid pin precise; it names Area center (a solid pin at the center of the matched road or area, which on a long road can be far from the vehicle), matching the legend and the Reporting Guide.
 *
 * v2.13.0 (exact pickup coordinates -- contracts assisted-entry-api v1.6.0,
 * entry-log-api v1.0.3, mo-report-api v1.0.6): 'dispatch.coordinates' (the
 * Latitude / Longitude pair in MapEntry, so the wizard AND the draft pane
 * render it -- MO-field parity) and 'mo.entry_coordinates' (the Entry
 * Detail modal's pair; no topic, because a "Learn more" link inside an
 * open, edited modal would navigate away unguarded, and the modal has no
 * map, address search or "Use address instead"). Untrue location copy
 * fixed: 'reports.include_legacy' (portal-native entries are not
 * "pin-confirmed"), 'reports.geo_plotting' (names exact coordinates and
 * hand-placed pins as precise; the gap is not only legacy rows),
 * 'reports.geo.min_accuracy' ("Geometric center +" keeps area-center and
 * better, not street-level; the top gate is "Rooftop or exact").
 *
 * v2.12.0 (Admin > People, contract admin-people-api v1.0.0): 'admin.people'
 * (the page-header identity note: one staff directory, logins attach to
 * it), 'admin.people.role_labels' (display-only labels; Dispatcher = entry
 * author); 'admin.linked_person' rewritten for the automatic link.
 *
 * v2.11.0 (Engine Performance -- contract performance-api v1.2.0 SS7,
 * decisions #180 + #185): 'engine.page' (what the surface measures),
 * 'engine.head_to_head' (why shared-corpus beats era totals),
 * 'engine.confounded' (the era-totals warning), 'engine.adjudication'
 * (human tiers + the 50-draft precision gate), 'engine.band' (the doc-71
 * monitoring thresholds).
 *
 * v2.10.0 (performance-api v1.1.0 metric #8): 'perf.cross_reviews' --
 * reviews of drafts naming another dispatcher (the review-side companion
 * of back-entries; also catches cross dismissals).
 *
 * v2.9.0 (Team Performance -- contract performance-api v1.0.0, decision
 * #180): 'perf.page' (what the page measures + the previous-period ghost),
 * 'perf.back_entries' (Robert's above-and-beyond metric -- entries submitted
 * for another dispatcher), 'perf.sla_saves' (reviews inside the sweeper's
 * shadow), 'perf.active_days' (observable-activity floor, not a clock).
 *
 * v2.8.0 (the /review split -- contract entry-log-api v1.0.0, AE slice 4):
 * 'mo.screened_out' (the why-chips + the not-in-flight rule) +
 * 'mo.entry_log' (the register + VOIDED/edited marks + Unlock-to-edit).
 *
 * v2.7.0 (Module Settings, contract module-settings-api v1.0.0):
 * 'admin.module_settings' -- the page-header explainer (behavior knobs vs
 * System Config's credentials; defaults vs overrides; every change audited
 * and undoable from the Audit Log).
 *
 * v2.6.0 (Slice C audit rollback, contract audit-rollback-api v1.0.0):
 * 'admin.audit_rollback' -- the Audit Log Undo column explainer (reversal
 * not erasure; the undo is itself audited; greyed rows carry the reason).
 *
 * v2.5.0 (error observability, contract error-observability-api v1.0.0):
 * 'admin.errors' -- the admin Errors view header explainer (browser + server
 * rows, the survives-connection-loss delivery, what client/server means).
 *
 * v2.4.0 (P9.6 waived payment state, contract v1.7.0 -- decision #168):
 * 'ri.waived' -- the waive-confirm sheet + Reports Waived column explainer
 * (attribution stands, no bonus; neutral tone; excluded from printable
 * reports); 'ri.unpaid' copy updated -- unpaid now EXCLUDES waived credits.
 *
 * v2.3.0 (roster role labels): 'roster.role_labels' -- the Add/Edit sheet's
 * non-exclusive Driver/Dispatcher/Manager label chips (contract v1.6.0 §3A;
 * replaces the standalone dispatcher checkbox whose roster.is_dispatcher
 * atom is retired from the view -- registry entry kept for reference).
 *
 * v2.2.0 (RI tooltip batch, OPERATIONS-S9 -- Robert: "didn't see any in
 * review incentives"): the Reports summary columns ('ri.mentions',
 * 'ri.unpaid', 'ri.paid', 'ri.last_mention' -- the mentions-vs-awaiting /
 * money-state / staleness explainers behind the Ashley first-login
 * confusion), the printable report's screen-only stats note
 * ('ri.print_stats'), and the queue flow ('ri.states' state-filter chips,
 * 'ri.credit_picker' driver picker preselect/lock semantics).
 *
 * v2.1.0 (P9.4): 'ri.suggested_pending' -- the awaiting-confirmation banner/
 * chip explainer (confirmed-only counts; confirm in the queue).
 *
 * v1.1.0: doc #853 backlog first pass -- dispatch wizard (reasons/sentinel,
 * quote vs competitor, callback potential), assisted-entry detail
 * (draft fields, est. lost), admin user editor (linked person, GoTo
 * extension). The dispatch.* keys ALSO render in the assisted-entry Draft
 * Fields pane (full MO-field parity, decision #140) -- same field, same tip.
 *
 * v1.2.0 (data-hygiene slice): 'reports.*' namespace -- the Include Legacy
 * Data section switch and the Geo "what plots here" explainer (the
 * management-confusion killer; written for a non-technical manager).
 *
 * v2.0.0 (reports help pass -- the two-layer help system): the full
 * manager-facing reporting tooltip set. New sub-namespaces:
 *   - 'reports.geo.*'    Loss Map controls (render type, color-by, weight,
 *                        min-accuracy, cell size, weak spots, timeline)
 *   - 'reports.grp.*'    one tip per Standard/Custom heading group (the 14
 *                        oracle groups; keys = the ReportSections anchor
 *                        minus its 'grp-' prefix, so views derive them)
 *   - 'reports.dq.*'     the 7 Data-Quality checks
 *   - 'reports.std.*' / 'reports.custom.*' / 'reports.data.*' view-level
 *   - 'reports.filters'  the shared FilterPanel
 * Every reports tip carries `link` -- the NEW deep-link field into the
 * in-section Reporting Guide ('/reports/help#<anchor>', rendered as
 * "More →"); `topic` keeps deep-linking the legacy /help section
 * ("Learn more →"). Copy rule: 1-3 plain sentences derived from the actual
 * implementation (lossMapModes.ts, compute.py, extras.py, formulas.py --
 * every claim traces to code), written for a non-technical manager; depth
 * lives in the guide, not here.
 *
 * THE central tooltip registry (decision #142): every <HelpTip k="…" /> in
 * the app resolves here. Adding/refining a tooltip is a CONTENT edit in this
 * one file — never view surgery. Rules:
 *
 *   - Keys are stable, namespaced by module: 'ri.*' review incentives,
 *     'roster.*' roster management, 'dispatch.*' MO entry, 'ae.*'
 *     assisted-entry, 'admin.*' user admin, 'reports.*' reporting.
 *   - Only NON-self-explanatory fields/buttons get a tip (Robert's rule) —
 *     if the label already says it, no entry.
 *   - `topic` (optional) deep-links "Learn more →" into the Help section as
 *     '/help/<category>/<topic>'.
 *   - `link` (optional, v2.0.0) deep-links "More →" to any in-app path incl.
 *     a #anchor (the Reporting Guide pattern). `link` wins over `topic`.
 *   - A key missing from this registry renders NOTHING (HelpTip guards), so
 *     views may reference keys before their content lands.
 *
 * Authoring guide: docs/ref/help-system.md.
 */

export interface HelpTipEntry {
  /** 1–3 plain sentences. No markdown — tooltips are glanceable. */
  text: string;
  /** Optional '/help/<category>/<topic>' deep link ("Learn more →"). */
  topic?: string;
  /** Optional full in-app path incl. #anchor ("More →"); wins over topic. */
  link?: string;
}

export const HELP_TIPS: Record<string, HelpTipEntry> = {
  // ---- Review Incentives: queue -------------------------------------------
  'ri.suggestions': {
    text:
      'The portal reads each new review overnight and suggests who it praises ' +
      'by name, with the matching quote highlighted. Suggestions are advisory ' +
      '— you confirm every credit. high/medium chips come pre-selected; a low ' +
      'chip is a guess you opt into.',
    topic: 'review-incentives/queue-and-attribution',
  },
  'ri.mark_paid': {
    text:
      'Records the bonus as paid on a date (and an optional dollar amount for ' +
      'the accounting trail). Paid credits lock — they can only be removed by ' +
      'undoing the payment first.',
    topic: 'review-incentives/queue-and-attribution',
  },
  'ri.suggested_pending': {
    text:
      'The AI has spotted possible mentions in new reviews that nobody has ' +
      'confirmed yet. They never count in any report until you confirm them ' +
      'in the queue — that is why a person can show fewer mentions than you ' +
      'expect.',
    topic: 'review-incentives/reports',
  },
  'ri.states': {
    text:
      'Every review lands as Pending until you rule on it: Attribute credits ' +
      'the people named, No Staff Mentioned files it as Skipped. Nothing is ' +
      'final — Reopen puts a ruled review back to Pending.',
    topic: 'review-incentives/queue-and-attribution',
  },
  'ri.credit_picker': {
    text:
      '★ marks people the portal suggests for this review — confident ' +
      'suggestions come pre-selected, a low-confidence one is opt-in only. ' +
      'Attribute saves exactly the selection shown, replacing any unpaid ' +
      'credits; a paid credit is locked until its payment is undone.',
    topic: 'review-incentives/queue-and-attribution',
  },

  // ---- Review Incentives: reports -----------------------------------------
  'ri.mentions': {
    text:
      'Confirmed mentions only — reviews where this person was credited in ' +
      'the queue. AI-suggested mentions never count here until confirmed; ' +
      'they show separately as awaiting confirmation. A date range narrows ' +
      'this count to that window.',
    topic: 'review-incentives/reports',
  },
  'ri.unpaid': {
    text:
      'Confirmed mentions whose bonus has not been marked paid yet — the ' +
      'payout to-do list. Waived credits never count here — they sit in ' +
      'their own Waived column. Mark them paid from the person’s chip on ' +
      'the queue card.',
    topic: 'review-incentives/queue-and-attribution',
  },
  'ri.waived': {
    text:
      'Credits management decided not to pay — the person stays credited ' +
      'for the review, but no bonus is owed and it never counts as unpaid. ' +
      'Waived credits stay off printable reports entirely. Undo any time ' +
      'with Unwaive on the queue card.',
    topic: 'review-incentives/queue-and-attribution',
  },
  'ri.paid': {
    text:
      'Confirmed mentions already marked paid. “Total $ recorded” sums only ' +
      'payments where a dollar amount was entered — recording an amount is ' +
      'optional.',
    topic: 'review-incentives/reports',
  },
  'ri.last_mention': {
    text:
      'The date of this person’s newest confirmed mention. Suggestions ' +
      'awaiting confirmation never move it — that is why it can look stale ' +
      'while credits sit unconfirmed in the queue.',
    topic: 'review-incentives/reports',
  },
  'ri.print_stats': {
    text:
      'These totals are for you and stay on the screen — the printed page ' +
      'carries only the review list, ready to hand to the driver.',
    topic: 'review-incentives/reports',
  },

  // ---- Review Incentives: roster ------------------------------------------
  'roster.is_driver': {
    text:
      'On the review roster = can be credited (and bonused) when a Google ' +
      'review praises them by name. Everyone who talks to the public belongs ' +
      '— drivers, dispatchers, and office staff alike.',
    topic: 'review-incentives/roster',
  },
  'roster.is_dispatcher': {
    text:
      'Dispatchers author missed-opportunity entries. This flag controls entry ' +
      'attribution only — it has nothing to do with logins or permissions.',
    topic: 'review-incentives/roster',
  },
  'roster.identity': {
    text:
      'This list is people, not logins. Creating a person here does NOT create ' +
      'a portal account — accounts (passwords, roles) live in User Admin.',
    topic: 'review-incentives/roster',
  },
  'roster.role_labels': {
    text:
      'What this person does — pick any that apply. Labels are for display ' +
      'and reports only; they grant nothing and are separate from login ' +
      'accounts. The Dispatcher label also marks them as an entry author in ' +
      'the missed-opportunity tools.',
    topic: 'review-incentives/roster',
  },

  // ---- Dispatch wizard (also rendered by the assisted-entry Draft Fields
  //      pane — full MO-field parity, decision #140) ------------------------
  'dispatch.reasons': {
    text:
      'Pick every reason the caller actually gave — most misses have more ' +
      'than one. "Not specified" stands alone: choosing it clears the real ' +
      'reasons, and picking a real reason clears it.',
    topic: 'mo-entry/entering-an-mo',
  },
  'dispatch.quote_given': {
    text:
      'The price WE quoted, if one was given. A competitor’s price never ' +
      'goes here — that belongs in competitor quote. Use the note beside it ' +
      'for ranges or conditions ("350–450 depending on winch").',
    topic: 'mo-entry/entering-an-mo',
  },
  'dispatch.competitor_quote': {
    text:
      'What the caller said another company offered. Capturing it whenever ' +
      'you hear one is how the reports learn where we are losing on price.',
    topic: 'mo-entry/entering-an-mo',
  },
  'dispatch.callback_potential': {
    text:
      'Your 1–10 gut read on whether this caller would use us next time — ' +
      '1 barely, 10 almost certainly. A price-shopper who liked us runs ' +
      'high; a caller locked to another company runs low. Skip it if you ' +
      'have no read.',
    topic: 'mo-entry/entering-an-mo',
  },
  'dispatch.coordinates': {
    text:
      'Use these when the caller reads GPS coordinates aloud, or paste a map ' +
      'link from a texted pin drop: type or paste both numbers, like ' +
      '30.030731, -97.771006 (Texas longitudes are negative). Exact ' +
      'coordinates set the pin, and the address search will not move it. Tap ' +
      'Use address instead to put the pin back on the address, or clear both ' +
      'fields to remove the location.',
    topic: 'mo-entry/entering-an-mo',
  },

  // ---- Assisted-entry detail ----------------------------------------------
  'ae.draft_fields': {
    text:
      'Pre-filled by the portal from the call — check it like a draft, not a ' +
      'decision. Adjust anything that is off; corrections count on the ' +
      'Approve button and are kept alongside the original. Nothing becomes a ' +
      'real entry until you approve it.',
    topic: 'assisted-entry/reviewing-drafts',
  },
  'ae.est_lost': {
    text:
      'A rough dollar read on the missed job, based on what the call said ' +
      '(the quote discussed, or a typical price for that service). Use it to ' +
      'prioritize — it is not an accounting number.',
    topic: 'assisted-entry/reviewing-drafts',
  },

  // ---- MO: the /review split (slice 4, contract entry-log-api v1.0.0) ------
  'mo.screened_out': {
    text:
      'Calls that did not become an entry, with why: the assistant judged ' +
      'them not a missed opportunity ("AI: not applicable"), or a reviewer ' +
      'dismissed the draft (named, with the reason). Calls still waiting on ' +
      'a draft stay in the Entry Queue — they are not screened out. ' +
      '"Not yet screened" means no verdict exists yet.',
    topic: 'assisted-entry/reviewing-drafts',
  },
  'mo.entry_log': {
    text:
      'Every committed entry across the team for the date range — who ' +
      'entered what, when. Voided entries stay visible with a VOIDED mark; ' +
      'edited ones carry an "edited" tag. Open a row to view it; Unlock to ' +
      'edit — changes are tracked in the entry’s revision history.',
  },
  // No `topic`: a "Learn more" link inside an open, edited modal would
  // navigate away with no unsaved-changes guard.
  'mo.entry_coordinates': {
    text:
      'Type or paste both numbers, like 30.030731, -97.771006 (Texas ' +
      'longitudes are negative). Typed coordinates are saved as Exact ' +
      'coordinates. Clear both fields to remove the location.',
  },

  // ---- Team Performance (contract performance-api v1.0.0) -------------------
  'perf.page': {
    text:
      'Staff work over the selected window: draft reviews, entries, and ' +
      'presence. Cards compare against the previous period of the same ' +
      'length; the dashed chart line is that previous period. Numbers ' +
      'measure activity, not quality — read them together.',
  },
  'perf.back_entries': {
    text:
      'Entries this user submitted on ANOTHER dispatcher\'s behalf — the ' +
      'entry credits a different person than the one who typed it in. ' +
      'A high count usually means someone is covering calls beyond their ' +
      'own queue.',
  },
  'perf.cross_reviews': {
    text:
      'Draft reviews (approve, edit, or dismiss) this user handled where ' +
      'the call belonged to a DIFFERENT dispatcher — working the queue ' +
      'beyond their own calls. Approving or editing one also creates a ' +
      'back-entry; dismissals only show up here.',
  },
  'perf.sla_saves': {
    text:
      'Draft reviews completed in the final quarter of the SLA window — ' +
      'drafts that would soon have auto-expired. "Expired" has no owner: ' +
      'the sweeper dismisses those, so expiries count against the team, ' +
      'never a person.',
  },
  'perf.active_days': {
    text:
      'Days with any observable portal activity (sign-ins, reviews, ' +
      'entries). A floor, not a timesheet — staying signed in on a trusted ' +
      'device leaves fewer traces than working does.',
  },

  // ---- Admin: Errors view ---------------------------------------------------
  'admin.errors': {
    text:
      'Problems reported from both sides: "client" rows come from the ' +
      'person’s browser (sent even after a lost connection, so they can ' +
      'arrive minutes late), "server" rows are requests the portal itself ' +
      'rejected or failed. Click a row for the technical detail.',
    topic: 'admin/errors-log',
  },
  'admin.audit_rollback': {
    text:
      'Undo reverses an action (unpay a bonus, restore credits, reactivate ' +
      'an account, void an entry) — it never deletes history. The original ' +
      'event stays in the log and the undo is recorded as its own event. ' +
      'Greyed rows say why they can’t be undone.',
    topic: 'admin/audit-log',
  },

  // ---- Admin: Module Settings ----------------------------------------------
  'admin.module_settings': {
    text:
      'Behavior settings for each portal module — how entries, reports, and ' +
      'sessions act. Credentials and connections live in System Config, not ' +
      'here. A setting without an override runs on its default; every change ' +
      'is recorded in the Audit Log and can be undone from there.',
    topic: 'admin/module-settings',
  },

  // ---- User admin ----------------------------------------------------------
  'admin.linked_person': {
    text:
      'The person this login\'s entries and review credits attribute to. ' +
      'Left on automatic, a new login gets a person of the same name (or an ' +
      'unlinked one that already exists). Pick someone only for a second ' +
      'login or a rehire. People are managed under People.',
    topic: 'admin/users-and-access',
  },
  'admin.people': {
    text:
      'Everyone who works here, with or without a login — the one list that ' +
      'entries, the entry queue, PINs, and review credits attribute to. ' +
      'Creating a user adds its person here automatically; passwords and ' +
      'roles stay in User Admin.',
    topic: 'admin/users-and-access',
  },
  'admin.people.role_labels': {
    text:
      'What this person does — pick any that apply. Labels are for display ' +
      'and reports and grant nothing. The Dispatcher label is what puts them ' +
      'in the entry queue\'s dispatcher list.',
    topic: 'admin/users-and-access',
  },
  'admin.goto_extension': {
    text:
      'Their extension number on the company phone system — the same number ' +
      'call listings show beside each recording.',
    topic: 'admin/users-and-access',
  },

  // ==========================================================================
  // Reporting (two-layer help: these are the GLANCE layer; depth lives in
  // the Reporting Guide at /reports/help — every `link` lands on an anchor)
  // ==========================================================================

  // ---- Section-wide (ReportsLayout header) ---------------------------------
  'reports.include_legacy': {
    text:
      'Controls whether reports include entries migrated from the old ' +
      'missed-opportunities spreadsheet. Turn it OFF to analyze portal-native ' +
      'entries only, the cleaner cohort with more complete fields. One ' +
      'switch, every tab in this section at once; shared report links ' +
      'remember it.',
    link: '/reports/help#include-legacy',
  },

  // ---- Shared FilterPanel (Custom / Data / Geo) -----------------------------
  'reports.filters': {
    text:
      'Every filter you set must match at once (date range AND dispatcher ' +
      'AND reason…), while multiple picks inside one list mean any-of. ' +
      'Apply writes the filters into the page address, so the link is ' +
      'shareable and the back button retraces your steps.',
    link: '/reports/help#custom-filters',
  },

  // ---- Standard tab ---------------------------------------------------------
  'reports.std.overview': {
    text:
      'The at-a-glance band: six totals over every entry in scope, each with ' +
      'a 12-week trend line, plus auto-generated highlights underneath. ' +
      'Everything below breaks the same data down by time, cause, dispatcher, ' +
      'money, and location.',
    link: '/reports/help#std-overview',
  },

  // ---- Standard/Custom heading groups (keys = ReportSections anchor minus
  //      'grp-'; SECTION_TO_HEADING owns membership — types/reports.ts) ------
  'reports.grp.overview': {
    text:
      'The big picture: missed calls per month, by day of the week, and ' +
      'split across Day vs Night shift. Start here for volume and rhythm ' +
      'before drilling into causes.',
    link: '/reports/help#grp-overview',
  },
  'reports.grp.30-day-rolling-averages': {
    text:
      'Smooths day-to-day noise by averaging the trailing 30 days of data at ' +
      'each point — the line only moves when the underlying pattern moves. ' +
      'The summary table compares the current average against a month ' +
      'earlier.',
    link: '/reports/help#grp-30-day-rolling-averages',
  },
  'reports.grp.when-are-we-missing-calls': {
    text:
      'Two calendars of pain: a weekday-by-hour heatmap of when misses ' +
      'happen, and a day-by-day calendar of how many. Darker cells mean more ' +
      'missed calls in that slot.',
    link: '/reports/help#grp-when-are-we-missing-calls',
  },
  'reports.grp.coverage-capacity-analysis': {
    text:
      'Isolates misses whose reason points at coverage problems (no truck, ' +
      'no driver, capacity) or long ETAs, then splits them by shift, hour, ' +
      'and day. This is the staffing lens — spikes here suggest when we need ' +
      'more trucks available.',
    link: '/reports/help#grp-coverage-capacity-analysis',
  },
  'reports.grp.quote-outlier-analysis': {
    text:
      'Flags quotes far outside our normal range using a standard ' +
      'statistical fence, and shows how much they inflate the averages. ETA ' +
      'vs Quote plots those two numbers together for entries carrying both.',
    link: '/reports/help#grp-quote-outlier-analysis',
  },
  'reports.grp.duty-type-deep-dive': {
    text:
      'Misses by the kind of work requested: when each duty type happens ' +
      'across the day, plus a per-duty table of volume, lost revenue, ' +
      'typical quote, callback, and peak hour. "Typical quote" is the median ' +
      '— one giant winch-out will not skew it.',
    link: '/reports/help#grp-duty-type-deep-dive',
  },
  'reports.grp.why-are-we-missing-calls': {
    text:
      'The reason lens: the most common specific reasons, the duty mix, and ' +
      'how the top five reasons trend month over month. An entry with ' +
      'several reasons counts once under each, so reason counts can exceed ' +
      'entry counts.',
    link: '/reports/help#grp-why-are-we-missing-calls',
  },
  'reports.grp.financial-impact': {
    text:
      'The money lens: average quote by duty, whether price-driven losses ' +
      'cluster in certain price ranges, how quote values distribute, and how ' +
      'our quotes compared when the caller mentioned a competitor price.',
    link: '/reports/help#grp-financial-impact',
  },
  'reports.grp.dispatcher-analysis': {
    text:
      'Who took the missed calls: volume per dispatcher, the customer mix, ' +
      'and a per-dispatcher table (calls, average quote, callback, top ' +
      'reason, primary shift). Read it as workload insight, not a scoreboard ' +
      '— whoever answers the most calls also logs the most misses.',
    link: '/reports/help#grp-dispatcher-analysis',
  },
  'reports.grp.callback-opportunities': {
    text:
      'Entries worth chasing: how callback ratings distribute, the ' +
      'high-value list (callback 6+ with a real quote, biggest first), the ' +
      'quote-by-callback chase quadrant, and phone numbers that have missed ' +
      'us more than once.',
    link: '/reports/help#grp-callback-opportunities',
  },
  'reports.grp.company-account-analysis': {
    text:
      'Which companies we miss most, with total quote value and their top ' +
      'loss reason (name spellings are grouped automatically). The trend ' +
      'chart splits monthly volume by customer type.',
    link: '/reports/help#grp-company-account-analysis',
  },
  'reports.grp.lost-revenue-analysis': {
    text:
      'Puts dollars on each loss reason: total quoted value, call counts, ' +
      'and share of the total. Only entries that carry a quote contribute ' +
      'dollars — unquoted misses count calls but $0.',
    link: '/reports/help#grp-lost-revenue-analysis',
  },
  'reports.grp.week-over-week-trends': {
    text:
      'Recent momentum: the last 12 weeks of missed calls and lost revenue, ' +
      'plus an 8-week table with percent change from the week before. Green ' +
      'means down (fewer misses — good); red means up.',
    link: '/reports/help#grp-week-over-week-trends',
  },
  'reports.grp.winnable-opportunities': {
    text:
      'Every entry gets a 0–100 "winnable" score — how likely we could win ' +
      'this caller next time — from their callback rating (40%), the loss ' +
      'reason (30%), the quote size (20%), and how recent the call is (10%). ' +
      'This group shows the score spread and the top 15 to chase.',
    link: '/reports/help#grp-winnable-opportunities',
  },

  // ---- Data Quality tab (the 7 checks) --------------------------------------
  'reports.dq.score_breakdown': {
    text:
      'The inputs behind the Quality Score: completeness percentages and ' +
      'issue counts. The score starts at 100 and loses points for ' +
      'quote-format issues, missing reasons, and statistical outliers — 80 ' +
      'and up reads healthy.',
    link: '/reports/help#dq-score-breakdown',
  },
  'reports.dq.quote_issues': {
    text:
      'Entries whose quote arrived as text instead of one clean number ' +
      '("350-450 depending on winch") — the portal keeps the prose in a note ' +
      'and parses a best-guess number. Fix at the source: one number in the ' +
      'quote field, conditions in the note.',
    link: '/reports/help#dq-quote-issues',
  },
  'reports.dq.company_variations': {
    text:
      'The same company spelled more than one way ("ABC Towing" vs "ABC ' +
      'Towing LLC"). Reports group them automatically, but consistent naming ' +
      'keeps the company analysis sharp.',
    link: '/reports/help#dq-company-variations',
  },
  'reports.dq.fields_completeness': {
    text:
      'How often each of the six key fields is actually filled in. Low bars ' +
      'mean any report leaning on that field is working from a partial ' +
      'picture.',
    link: '/reports/help#dq-fields-completeness',
  },
  'reports.dq.missing_by_dispatcher': {
    text:
      'Per dispatcher: how often their entries carry a quote and a specific ' +
      'reason, sorted so the lowest quote-capture rate tops the list. A ' +
      'coaching tool for data capture, not a performance ranking.',
    link: '/reports/help#dq-missing-by-dispatcher',
  },
  'reports.dq.statistical_outliers': {
    text:
      'Quotes far outside the normal range for this data set — the same ' +
      'statistical fence the Standard report uses. Verify these are real ' +
      'quotes and not typos; each one also nudges the Quality Score down.',
    link: '/reports/help#dq-statistical-outliers',
  },
  'reports.dq.recent_issues': {
    text:
      'Entries from the last 30 days with any data problem: quote ' +
      'formatting, a missing reason, or no quote at all. The fix-it-now list ' +
      '— recent entries are the easiest to correct from memory.',
    link: '/reports/help#dq-recent-issues',
  },

  // ---- Custom tab ------------------------------------------------------------
  'reports.custom.comparison': {
    text:
      'Compares your filtered slice against the whole data set: how many ' +
      'records matched, what share of the total that is, and the quoted ' +
      'dollars each represents. The bar underneath draws that share to ' +
      'scale.',
    link: '/reports/help#custom-comparison',
  },
  'reports.custom.sample': {
    text:
      'The first 10 entries that matched your filters, so you can ' +
      'sanity-check the slice the charts are built from. For the full list ' +
      'use the Data tab — the same filters carry over.',
    link: '/reports/help#custom-sample',
  },

  // ---- Data tab ---------------------------------------------------------------
  'reports.data.grid': {
    text:
      'Every entry in scope as a sortable, pageable table. Display Columns ' +
      'picks which of the 16 columns show; Export CSV downloads the current ' +
      'filtered set — everything the grid matches, not just this page.',
    link: '/reports/help#data',
  },

  // ---- Geo tab: what plots (v1.2.0, now guide-linked) -------------------------
  'reports.geo_plotting': {
    text:
      'Every pin is the pickup location saved with the entry, not the ' +
      'caller’s home address. Solid pins are exact coordinates, a rooftop or ' +
      'street-level address match, a hand-placed pin, or the center of the ' +
      'matched road or area (on a long road that can be far from the ' +
      'vehicle). Hollow dashed pins are approximate, the general area ' +
      'only. Entries without a saved location never appear on the map, which ' +
      'is why the pin count can be far below the entry count. Grid and Heat ' +
      'group nearby pins into cells (½ to 2 mi) and shade them by the ' +
      'selected metric.',
    link: '/reports/help#geo-what-plots',
  },

  // ---- Geo tab: Loss Map controls ---------------------------------------------
  'reports.geo.render_type': {
    text:
      'Pins shows each entry individually, color-coded and clustered until ' +
      'you zoom in. Heat highlights where entries concentrate; Grid shades ' +
      'square cells so areas compare apples-to-apples — both weighted by the ' +
      'selected metric. Footprint outlines the outer boundary of everywhere ' +
      'we have missed work, with 25/50/80-mile rings from HQ.',
    link: '/reports/help#geo-render-types',
  },
  'reports.geo.color_by': {
    text:
      'Chooses what each pin’s color means: loss reason, duty, shift, ' +
      'callback band, or quote bucket. The legend below the map is the color ' +
      'key — click a category there to hide or show it. Colors match the ' +
      'report charts, so the same reason is the same color everywhere.',
    link: '/reports/help#geo-color-by',
  },
  'reports.geo.weight_by': {
    text:
      'Chooses the number Heat and Grid visualize: how many calls we missed, ' +
      'the dollars quoted on them, or the average quote. Grid shades each ' +
      'cell amber-to-red relative to the heaviest cell currently on the map.',
    link: '/reports/help#geo-color-by',
  },
  'reports.geo.min_accuracy': {
    text:
      'Hides entries whose saved location is less precise than the tier you ' +
      'pick. "Geometric center +" keeps area-center fixes and better; ' +
      '"Rooftop or exact" keeps exact coordinates and rooftop address ' +
      'matches. Hand-placed pins count as street-level. The Location ' +
      'precision strip under the map shows each tier’s count; excluded ' +
      'tiers appear crossed out.',
    link: '/reports/help#geo-accuracy',
  },
  'reports.geo.cell_size': {
    text:
      'Sets how large an area each Heat/Grid cell covers: roughly half a ' +
      'mile, one mile, or two miles. Smaller cells pinpoint streets but ' +
      'scatter the data; larger cells show broader trouble zones. Weak Spots ' +
      're-ranks with whatever size you pick.',
    link: '/reports/help#geo-cells',
  },
  'reports.geo.weak_spots': {
    text:
      'Ranks the map’s cells by the selected metric so the worst areas ' +
      'surface first — each row carries the cell’s coordinates, its value, ' +
      'and its most common loss reason. Tap a row to zoom the map there. ' +
      'Only entries currently plotted count: filters, the timeline, and the ' +
      'accuracy gate all apply.',
    link: '/reports/help#geo-weak-spots',
  },
  'reports.geo.timeline': {
    text:
      'Narrows the map to a month range without touching your filters — ' +
      'drag the handles, or press play to step a one-month window across ' +
      'time. The mini bars show how many plotted entries each month holds.',
    link: '/reports/help#geo-timeline',
  },

  // -- Engine Performance (performance-api v1.2.0 SS7) ----------------------
  'engine.page': {
    text:
      'Tracks the judging engine itself, not the people: which prompt/model ' +
      'configuration screened each call, how configurations compare on the ' +
      'same calls, and how reviewers ruled on what each one drafted. Every ' +
      'verdict is logged permanently, so these numbers are replayable ' +
      'history, not estimates.',
  },
  'engine.head_to_head': {
    text:
      'Compares two configurations on the SAME calls — only recordings both ' +
      'have judged, latest verdict each side. That isolates the prompt ' +
      'change from traffic differences. A “flip” is a call the older config ' +
      'passed on that the newer one drafted; a reverse flip (newer config ' +
      'downgrading) is the warning sign.',
  },
  'engine.confounded': {
    text:
      'Era totals mix different weeks of traffic — a config that ran during ' +
      'a busy stretch looks different for reasons that have nothing to do ' +
      'with the prompt. Use them for scale only; trust the head-to-head ' +
      'numbers for quality comparisons.',
  },
  'engine.adjudication': {
    text:
      'How humans ruled on the drafts each configuration produced: approved ' +
      'clean, approved with edits (and how many fields needed fixing), or ' +
      'dismissed (and why). The “of 50” counter is the gate for the next ' +
      'prompt change — precision can’t be judged until enough drafts have a ' +
      'human verdict.',
  },
  'engine.band': {
    text:
      'Expected daily ranges from the first weeks of live data: drafts per ' +
      'business day, share of judged calls that draft, and how many land ' +
      '“unclear.” Outside the band two days running usually means a prompt, ' +
      'cadence, or transcript-quality problem — not a staffing one. ' +
      'Provisional until more weeks accumulate.',
  },
};
